import { useMemo, useState } from 'react';
import { Network, TriangleAlert } from '@signozhq/icons';
import { Button } from '@signozhq/ui/button';
import { prepareScalarTables } from '../../../queryV5/prepareScalarTables';
import { getScalarResults } from '../../../queryV5/v5ResponseData';
import PanelMessage from '../../components/PanelMessage/PanelMessage';
import type { PanelRendererProps } from '../../types/rendererProps';
import ConnectionList from './ConnectionList';
import GraphCanvas from './GraphCanvas';
import { buildGraph } from './graphData';
import { graphPositions } from './graphLayout';
import type { GraphPositions } from './types';
import { useGraphLayout } from './useGraphLayout';
import styles from './NodeGraphPanel.module.scss';

function NodeGraphPanelRenderer({
	panel,
	data,
	isFetching,
	refetch,
	onSaveNodePositions,
}: PanelRendererProps<'signoz/NodeGraphPanel'>): JSX.Element {
	const settings = panel.spec.plugin.spec;
	const graph = useMemo(
		() =>
			buildGraph(
				prepareScalarTables({
					results: getScalarResults(data.response),
					legendMap: data.legendMap,
					requestPayload: data.requestPayload,
				}),
				settings,
			),
		[data.response, data.legendMap, data.requestPayload, settings],
	);
	const [selection, select] = useState<{ kind: 'node' | 'edge'; id: string }>();
	const [view, setView] = useState<'graph' | 'list'>('graph');
	const layout = useGraphLayout(onSaveNodePositions);
	const visiblePositions = useMemo(() => {
		if (graph.status !== 'ready') {
			return {};
		}
		const saved =
			layout.state.status === 'view' ? settings.positions : layout.state.positions;
		return graphPositions(graph.nodes, saved, graph.edges);
	}, [graph, settings.positions, layout.state]);

	if (graph.status === 'invalid') {
		return (
			<PanelMessage
				icon={<TriangleAlert size={18} />}
				title="Check graph data"
				description={graph.message}
				tone="danger"
				data-testid="node-graph-invalid"
			/>
		);
	}
	if (!graph.edges.length) {
		let description =
			'No observed connections in this time range. Extend the range or change the query.';
		if (graph.omitted) {
			description =
				'The node limit excludes every connection. Increase the maximum nodes in panel settings.';
		}
		return (
			<PanelMessage
				icon={<Network size={18} />}
				title="No connections"
				description={description}
				action={refetch ? { label: 'Retry', onClick: refetch } : undefined}
				data-testid="node-graph-empty"
			/>
		);
	}

	const hasSelectedNode =
		selection?.kind === 'node' &&
		graph.nodes.some((node) => node.id === selection.id);
	const selectedNode = hasSelectedNode ? selection.id : undefined;
	const edges = graph.edges.filter(
		(edge) =>
			!selectedNode ||
			edge.source === selectedNode ||
			edge.target === selectedNode,
	);
	const selectedEdge = selection?.kind === 'edge' ? selection.id : undefined;
	const canMove =
		!!onSaveNodePositions &&
		(layout.state.status === 'editing' || layout.state.status === 'error');
	const startLayout = (): void => {
		const positions: GraphPositions = {
			...settings.positions,
			...visiblePositions,
		};
		layout.dispatch({ type: 'start', positions });
	};

	return (
		<div
			className={styles.container}
			aria-busy={isFetching}
			data-testid="node-graph-panel"
		>
			<div className={styles.toolbar}>
				<span className={styles.summary}>
					{graph.nodes.length} nodes · {graph.edges.length} connections
				</span>
				<div className={styles.actions}>
					<div className={`${styles.actions} ${styles.graphOnly}`}>
						<Button
							variant="ghost"
							color="secondary"
							size="sm"
							testId="node-graph-view"
							onClick={() => setView(view === 'graph' ? 'list' : 'graph')}
							disabled={layout.state.status !== 'view'}
							disabledTooltip="Save or cancel the layout first"
						>
							{view === 'graph' ? 'Connection list' : 'Show graph'}
						</Button>
						{onSaveNodePositions &&
							view === 'graph' &&
							layout.state.status === 'view' && (
								<Button
									variant="outlined"
									color="secondary"
									size="sm"
									testId="node-graph-arrange"
									onClick={startLayout}
								>
									Arrange
								</Button>
							)}
					</div>
					{layout.state.status !== 'view' && (
						<>
							<Button
								variant="solid"
								color="primary"
								size="sm"
								testId="node-graph-save"
								onClick={() => {
									void layout.save();
								}}
								loading={layout.state.status === 'saving'}
							>
								Save layout
							</Button>
							<Button
								variant="ghost"
								color="secondary"
								size="sm"
								testId="node-graph-cancel"
								disabled={layout.state.status === 'saving'}
								disabledTooltip="Layout is being saved"
								onClick={() => layout.dispatch({ type: 'finish' })}
							>
								Cancel
							</Button>
						</>
					)}
				</div>
			</div>
			{graph.omitted > 0 && (
				<output className={styles.notice}>
					{graph.omitted} connections hidden by the node/400-connection limit. Narrow
					the query or raise the node limit.
				</output>
			)}
			{canMove && (
				<p className={styles.hint}>
					Drag nodes or focus a node and use arrow keys. Save applies to this
					dashboard.
				</p>
			)}
			{layout.state.status === 'error' && (
				<p className={styles.notice} role="alert">
					{layout.state.message}
				</p>
			)}
			<div className={styles.content} data-view={view}>
				{view === 'graph' && (
					<GraphCanvas
						nodes={graph.nodes}
						edges={graph.edges}
						positions={visiblePositions}
						selectedEdge={selectedEdge}
						selectedNode={selectedNode}
						onSelectEdge={(id) => select({ kind: 'edge', id })}
						onSelectNode={(id) => select({ kind: 'node', id })}
						onMove={
							canMove
								? (id, position) => layout.dispatch({ type: 'move', id, position })
								: undefined
						}
					/>
				)}
				<div className={styles.sidebar}>
					{selectedNode && (
						<Button
							variant="ghost"
							color="secondary"
							size="sm"
							testId="node-graph-clear"
							onClick={() => select(undefined)}
						>
							Show all connections
						</Button>
					)}
					<ConnectionList
						edges={edges}
						selectedEdge={selectedEdge}
						onSelect={(id) => select({ kind: 'edge', id })}
					/>
				</div>
			</div>
			<p className={styles.legend}>
				{`Caller → callee · Error rate: warning >${settings.warningThreshold ?? 5}%, critical >${settings.criticalThreshold ?? 10}% · Missing metrics shown as —`}
			</p>
		</div>
	);
}

export default NodeGraphPanelRenderer;
